import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

import { Loadable } from '@rygen/rygen-ui/model';

import {
  RateQuote,
  RateShop,
  RateUsage,
  RoutingGuideRule,
  type ServiceTypeFilters,
  WellKnownRateStrategy,
} from '@/model';

export interface UseRoutingGuideResponse {
  excludedRates: ComputedRef<RateQuote[]>;
  filteredExcludedRates: ComputedRef<RateQuote[]>;
  filteredPreferredRates: ComputedRef<RateQuote[]>;
  filteredStandardRates: ComputedRef<RateQuote[]>;
  preferredRates: ComputedRef<RateQuote[]>;
  routingGuideRule: ComputedRef<RoutingGuideRule | undefined>;
  standardRates: ComputedRef<RateQuote[]>;
}

export const useRoutingGuide = (
  rateShop: MaybeRefOrGetter<Loadable<RateShop>>,
  serviceTypeFilters: MaybeRefOrGetter<ServiceTypeFilters>,
): UseRoutingGuideResponse => {
  const routingGuideRule = computed(() => {
    const rawRateShop = toValue(rateShop);
    return rawRateShop.data?.routingGuideRule;
  });

  const isUnfiltered = computed(() => {
    const rawFilters = toValue(serviceTypeFilters);
    return Object.entries(rawFilters)
      .filter(([key, _value]) => key !== WellKnownRateStrategy.EXCLUDED)
      .map(([_key, value]) => value)
      .every((st) => !st);
  });

  const preferredRates = computed(() => {
    const rawRateShop = toValue(rateShop);
    return (
      rawRateShop.data?.preferredQuotes.filter(
        (rq) => rq.rateUsage === RateUsage.RATE_SHOP,
      ) ?? []
    );
  });

  const filteredPreferredRates = computed(() => {
    const rawFilters = toValue(serviceTypeFilters);
    return preferredRates.value.filter((r) => {
      return isUnfiltered.value || rawFilters[r.serviceTypeForFiltering];
    });
  });

  const standardRates = computed(() => {
    const rawRateShop = toValue(rateShop);
    return (
      rawRateShop.data?.standardQuotes.filter(
        (rq) => rq.rateUsage === RateUsage.RATE_SHOP,
      ) ?? []
    );
  });

  const filteredStandardRates = computed(() => {
    const rawFilters = toValue(serviceTypeFilters);
    return standardRates.value.filter((r) => {
      return isUnfiltered.value || rawFilters[r.serviceTypeForFiltering];
    });
  });

  const excludedRates = computed(() => {
    const rawRateShop = toValue(rateShop);
    return rawRateShop.data?.excludedQuotes ?? [];
  });

  const filteredExcludedRates = computed(() => {
    const rawFilters = toValue(serviceTypeFilters);
    return excludedRates.value.filter((r) => {
      return isUnfiltered.value || rawFilters[r.serviceTypeForFiltering];
    });
  });

  return {
    excludedRates,
    filteredExcludedRates,
    filteredPreferredRates,
    filteredStandardRates,
    preferredRates,
    routingGuideRule,
    standardRates,
  };
};
